/**
 * IPC CN61 CSV Utilities
 * Handles CSV operations for IPC CN61 data
 */

export interface IpcCn61CsvRow {
  do_from: string;
  do_to: string;
  year: string;
  service: string;
  product: string;
  rg: string;
  td_rate_kg: string;
  td_rate_item: string;
}

export interface IpcCn61RateData {
  direction: 'inbound' | 'outbound';
  format: string;
  type: 'weight' | 'items';
  rate: number;
  service: 'Priority' | 'Non-Priority';
}

/**
 * Parse CSV string for IPC CN61 data
 * @param csvString - CSV content as string
 * @param currentData - Current CN61 data structure
 * @param currentUserPpo - Current user's designated operator
 * @param expectedYear - Expected year to validate against
 * @returns Parsed CN61 rate data
 */
export function parseIpcCn61Csv(csvString: string, currentData?: any, currentUserPpo?: string, expectedYear?: number): IpcCn61RateData[] {
  const lines = csvString.trim().split('\n');
  
  if (lines.length < 2) {
    throw new Error('CSV file must contain at least a header row and one data row');
  }

  // Parse header
  const headers = lines[0].split(',').map(h => h.trim());
  const expectedHeaders = ['do_from', 'do_to', 'year', 'service', 'product', 'rg', 'td_rate_kg', 'td_rate_item'];
  
  if (!expectedHeaders.every(header => headers.includes(header))) {
    throw new Error('CSV file must contain the required headers: do_from, do_to, year, service, product, rg, td_rate_kg, td_rate_item');
  }

  const data: IpcCn61RateData[] = [];
  const errors: string[] = [];

  function sanitizeNumericCell(cell: string): string {
    if (cell == null) return '';
    const trimmed = cell.trim();
    // Remove leading apostrophe used to force Excel text formatting
    if (trimmed.startsWith("'")) return trimmed.slice(1);
    // Handle Excel formula-style text wrapper: ="2.000"
    if (/^=\".*\"$/.test(trimmed)) {
      return trimmed.substring(2, trimmed.length - 1);
    }
    return trimmed;
  }

  function validateThreeDecimals(cell: string, fieldName: 'td_rate_kg' | 'td_rate_item', rowIdx: number): boolean {
    if (cell === '') return true; // allow empty as zero
    const threeDecimalPattern = /^\d+(\.\d{3})$/;
    if (!threeDecimalPattern.test(cell)) {
      errors.push(`Row ${rowIdx}: ${fieldName} must have exactly 3 decimal places (e.g., 2.000)`);
      return false;
    }
    return true;
  }
  
  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const values = line.split(',').map(v => v.trim());
    
    if (values.length !== expectedHeaders.length) {
      errors.push(`Row ${i + 1}: Invalid number of columns. Expected ${expectedHeaders.length}, got ${values.length}`);
      continue; // Skip this row but continue validation
    }

    const row: IpcCn61CsvRow = {
      do_from: values[0],
      do_to: values[1],
      year: values[2],
      service: values[3],
      product: values[4],
      rg: values[5],
      td_rate_kg: values[6],
      td_rate_item: values[7]
    };

    let hasRowError = false;

    // Validate required fields
    if (!row.do_from || !row.do_to || !row.year || !row.service || !row.product) {
      errors.push(`Row ${i + 1}: Missing required fields`);
      hasRowError = true;
    }

    // Validate year if expectedYear is provided
    if (expectedYear !== undefined) {
      const rowYear = parseInt(row.year, 10);
      if (isNaN(rowYear)) {
        errors.push(`Row ${i + 1}: Invalid year value '${row.year}'. Must be a valid number`);
        hasRowError = true;
      } else if (rowYear !== expectedYear) {
        errors.push(`Row ${i + 1}: Year mismatch. Expected ${expectedYear} but got ${rowYear}`);
        hasRowError = true;
      }
    }

    // Validate service
    const validServices = ['Priority', 'Non-Priority'];
    if (!validServices.includes(row.service)) {
      errors.push(`Row ${i + 1}: Invalid service. Must be one of: ${validServices.join(', ')}`);
      hasRowError = true;
    }

    // Validate product
    const validProducts = ['Format P', 'Format G', 'Format E', 'Format X', 'Postcards', 'Registered', 'Insured', 'Express', 'M-Bags'];
    if (!validProducts.includes(row.product)) {
      errors.push(`Row ${i + 1}: Invalid product. Must be one of: ${validProducts.join(', ')}`);
      hasRowError = true;
    }

    // Sanitize and validate exact 3-decimal inputs
    const tdRateKgStr = sanitizeNumericCell(row.td_rate_kg);
    const tdRateItemStr = sanitizeNumericCell(row.td_rate_item);
    const kgValid = validateThreeDecimals(tdRateKgStr, 'td_rate_kg', i + 1);
    const itemValid = validateThreeDecimals(tdRateItemStr, 'td_rate_item', i + 1);
    
    if (!kgValid || !itemValid) {
      hasRowError = true;
    }

    // Parse rates - treat empty strings as 0
    const tdRateKg = tdRateKgStr === '' ? 0 : parseFloat(tdRateKgStr);
    const tdRateItem = tdRateItemStr === '' ? 0 : parseFloat(tdRateItemStr);

    if (isNaN(tdRateKg) || tdRateKg < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_kg value. Must be a non-negative number`);
      hasRowError = true;
    }

    if (isNaN(tdRateItem) || tdRateItem < 0) {
      errors.push(`Row ${i + 1}: Invalid td_rate_item value. Must be a non-negative number`);
      hasRowError = true;
    }

    // Skip adding data if this row has errors
    if (hasRowError) {
      continue;
    }

    // Determine direction based on do_from and do_to
    // For inbound: do_from = filter DO (other PPO), do_to = current user DO (receiving from other)
    // For outbound: do_from = current user DO, do_to = filter DO (other PPO) (sending to other)
    const filterPpo = currentData?.dto?.ppo || 'UNKNOWN';
    const userPpo = currentUserPpo || filterPpo;
    
    // If do_from is the filter DO (other PPO), it's inbound; if do_from is the current user DO, it's outbound
    const direction = row.do_from === filterPpo ? 'inbound' : 'outbound';

    // Determine which fields to create based on product type
    const isItemsOnlyProduct = ['Registered', 'Insured', 'Express'].includes(row.product);
    const isWeightOnlyProduct = row.product === 'M-Bags';
    const isFormatProduct = ['Format P', 'Format G', 'Format E', 'Format X', 'Postcards'].includes(row.product);

    // Add rows based on product type
    if (isItemsOnlyProduct) {
      // Registered, Insured, Express - only items
      if (tdRateItem > 0 || tdRateItem === 0) { // Include even if zero
        data.push({
          direction,
          format: row.product,
          type: 'items',
          rate: tdRateItem,
          service: row.service as 'Priority' | 'Non-Priority'
        });
      }
    } else if (isWeightOnlyProduct) {
      // M-Bags - only weight
      if (tdRateKg > 0 || tdRateKg === 0) { // Include even if zero
        data.push({
          direction,
          format: row.product,
          type: 'weight',
          rate: tdRateKg,
          service: row.service as 'Priority' | 'Non-Priority'
        });
      }
    } else if (isFormatProduct) {
      // Format P/G/E/X, Postcards - both weight and items (only if they have values)
      if (tdRateKg > 0 || tdRateKg === 0) { // Include even if zero
        data.push({
          direction,
          format: row.product,
          type: 'weight',
          rate: tdRateKg,
          service: row.service as 'Priority' | 'Non-Priority'
        });
      }
      if (tdRateItem > 0 || tdRateItem === 0) { // Include even if zero
        data.push({
          direction,
          format: row.product,
          type: 'items',
          rate: tdRateItem,
          service: row.service as 'Priority' | 'Non-Priority'
        });
      }
    }
  }

  // If there were any errors during validation, throw them all
  if (errors.length > 0) {
    throw new Error(errors.join('|||')); // Use ||| as delimiter to split later
  }

  return data;
}

/**
 * Convert IPC CN61 data to CSV format
 * @param data - CN61 data structure
 * @param currentUserPpo - Current user's designated operator
 * @returns CSV string
 */
export function convertIpcCn61DataToCsv(data: any, currentUserPpo?: string): string {
  const csvRows: string[] = [];
  
  // Add header
  csvRows.push('do_from,do_to,year,service,product,rg,td_rate_kg,td_rate_item');
  
  const filterPpo = data?.dto?.ppo || 'UNKNOWN';
  const userPpo = currentUserPpo || filterPpo;
  const year = data?.dto?.year || new Date().getFullYear();

  // Ensure values are consistently formatted with 3 decimals for download
  function formatTo3Decimals(value: any): string {
    if (value === null || value === undefined || value === '') return '0.000';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0.000';
    return Number(num).toFixed(3);
  }

  // Format value to exactly 3 decimals for CSV content
  function formatCsvValueForExport(value: any): string {
    return formatTo3Decimals(value);
  }

  // Process inbound data
  if (data.request?.inboundCn61Input) {
    const inbound = data.request.inboundCn61Input;
    
    // Define all possible fields to ensure they're all included
    const allPossibleFields = ['formatP', 'formatG', 'formatE', 'formatX', 'postcards', 'registered', 'insured', 'express', 'mbags'];
    const fieldsToProcess = [...new Set([...Object.keys(inbound), ...allPossibleFields])];
    
    // Process all format types dynamically
    fieldsToProcess.forEach(key => {
      const format = inbound[key];
      
      let product = '';
      let hasWeight = false;
      let hasItems = false;
      
      // Determine product type and what data is available
      if (key.startsWith('format')) {
        product = `Format ${key.replace('format', '')}`;
        hasWeight = format && format.weight && (format.weight.priority > 0 || format.weight.nonPriority > 0);
        hasItems = format && format.items && (format.items.priority > 0 || format.items.nonPriority > 0);
      } else if (key === 'postcards') {
        product = 'Postcards';
        hasWeight = format && format.weight && (format.weight.priority > 0 || format.weight.nonPriority > 0);
        hasItems = format && format.items && (format.items.priority > 0 || format.items.nonPriority > 0);
      } else if (key === 'registered') {
        product = 'Registered';
        // Registered is items-only - always check items structure, ignore weight
        hasItems = format && format.items && (format.items.priority !== null && format.items.priority !== undefined || format.items.nonPriority !== null && format.items.nonPriority !== undefined);
        hasWeight = false; // Never export weight for registered
      } else if (key === 'insured') {
        product = 'Insured';
        // Insured is items-only - always check items structure, ignore weight
        hasItems = format && format.items && (format.items.priority !== null && format.items.priority !== undefined || format.items.nonPriority !== null && format.items.nonPriority !== undefined);
        hasWeight = false; // Never export weight for insured
      } else if (key === 'express') {
        product = 'Express';
        // Express is items-only - always check items structure, ignore weight
        hasItems = format && format.items && (format.items.priority !== null && format.items.priority !== undefined || format.items.nonPriority !== null && format.items.nonPriority !== undefined);
        hasWeight = false; // Never export weight for express
      } else if (key === 'mbags') {
        product = 'M-Bags';
        // M-Bags is weight-only - always check weight structure, ignore items
        hasWeight = format && format.weight && (format.weight.priority !== null && format.weight.priority !== undefined || format.weight.nonPriority !== null && format.weight.nonPriority !== undefined);
        hasItems = false; // Never export items for mbags
      }
      
      // Generate CSV rows for available data - separate priority and non-priority
      if (['registered', 'insured', 'express', 'mbags'].includes(key)) {
        // Special case for registered, insured, express, mbags
        // These items need TWO rows each (Priority and Non-Priority)
        if (key === 'mbags') {
          // M-Bags has weight column filled, items column empty
          const weightPriority = format.weight?.priority ?? 0;
          const weightNonPriority = format.weight?.nonPriority ?? 0;
          const weightPriorityValue = formatCsvValueForExport(weightPriority);
          const weightNonPriorityValue = formatCsvValueForExport(weightNonPriority);
          csvRows.push(`${filterPpo},${userPpo},${year},Priority,${product},,${weightPriorityValue},0.000`);
          csvRows.push(`${filterPpo},${userPpo},${year},Non-Priority,${product},,${weightNonPriorityValue},0.000`);
        } else {
          // Registered, Insured, Express have items column filled, weight column empty
          const itemPriority = format.items?.priority ?? 0;
          const itemNonPriority = format.items?.nonPriority ?? 0;
          const itemPriorityValue = formatCsvValueForExport(itemPriority);
          const itemNonPriorityValue = formatCsvValueForExport(itemNonPriority);
          csvRows.push(`${filterPpo},${userPpo},${year},Priority,${product},,0.000,${itemPriorityValue}`);
          csvRows.push(`${filterPpo},${userPpo},${year},Non-Priority,${product},,0.000,${itemNonPriorityValue}`);
        }
      } else if (hasWeight && hasItems) {
        // Both weight and items available - generate separate rows for priority and non-priority
        const weightPriority = format.weight?.priority || 0;
        const weightNonPriority = format.weight?.nonPriority || 0;
        const itemPriority = format.items?.priority || 0;
        const itemNonPriority = format.items?.nonPriority || 0;
        
        // Priority row
        const weightValue = formatCsvValueForExport(weightPriority);
        const itemValue = formatCsvValueForExport(itemPriority);
        csvRows.push(`${filterPpo},${userPpo},${year},Priority,${product},,${weightValue},${itemValue}`);
        
        // Non-Priority row
        const weightValueNP = formatCsvValueForExport(weightNonPriority);
        const itemValueNP = formatCsvValueForExport(itemNonPriority);
        csvRows.push(`${filterPpo},${userPpo},${year},Non-Priority,${product},,${weightValueNP},${itemValueNP}`);
      } else if (hasWeight) {
        // Only weight available - generate separate rows for priority and non-priority
        const weightPriority = format.weight?.priority || 0;
        const weightNonPriority = format.weight?.nonPriority || 0;
        
        // Priority row
        csvRows.push(`${filterPpo},${userPpo},${year},Priority,${product},,${formatCsvValueForExport(weightPriority)},0.000`);
        // Non-Priority row
        csvRows.push(`${filterPpo},${userPpo},${year},Non-Priority,${product},,${formatCsvValueForExport(weightNonPriority)},0.000`);
      } else if (hasItems) {
        // Only items available - generate separate rows for priority and non-priority
        const itemPriority = format.items?.priority || 0;
        const itemNonPriority = format.items?.nonPriority || 0;
        
        // Priority row
        csvRows.push(`${filterPpo},${userPpo},${year},Priority,${product},,0.000,${formatCsvValueForExport(itemPriority)}`);
        // Non-Priority row
        csvRows.push(`${filterPpo},${userPpo},${year},Non-Priority,${product},,0.000,${formatCsvValueForExport(itemNonPriority)}`);
      }
    });
  }

  // Process outbound data
  if (data.request?.outboundCn61Input) {
    const outbound = data.request.outboundCn61Input;
    
    // Define all possible fields to ensure they're all included
    const allPossibleFields = ['formatP', 'formatG', 'formatE', 'formatX', 'postcards', 'registered', 'insured', 'express', 'mbags'];
    const fieldsToProcess = [...new Set([...Object.keys(outbound), ...allPossibleFields])];
    
    // Process all format types dynamically
    fieldsToProcess.forEach(key => {
      const format = outbound[key];
      
      let product = '';
      let hasWeight = false;
      let hasItems = false;
      
      // Determine product type and what data is available
      if (key.startsWith('format')) {
        product = `Format ${key.replace('format', '')}`;
        hasWeight = format && format.weight && (format.weight.priority > 0 || format.weight.nonPriority > 0);
        hasItems = format && format.items && (format.items.priority > 0 || format.items.nonPriority > 0);
      } else if (key === 'postcards') {
        product = 'Postcards';
        hasWeight = format && format.weight && (format.weight.priority > 0 || format.weight.nonPriority > 0);
        hasItems = format && format.items && (format.items.priority > 0 || format.items.nonPriority > 0);
      } else if (key === 'registered') {
        product = 'Registered';
        // Registered is items-only - always check items structure, ignore weight
        hasItems = format && format.items && (format.items.priority !== null && format.items.priority !== undefined || format.items.nonPriority !== null && format.items.nonPriority !== undefined);
        hasWeight = false; // Never export weight for registered
      } else if (key === 'insured') {
        product = 'Insured';
        // Insured is items-only - always check items structure, ignore weight
        hasItems = format && format.items && (format.items.priority !== null && format.items.priority !== undefined || format.items.nonPriority !== null && format.items.nonPriority !== undefined);
        hasWeight = false; // Never export weight for insured
      } else if (key === 'express') {
        product = 'Express';
        // Express is items-only - always check items structure, ignore weight
        hasItems = format && format.items && (format.items.priority !== null && format.items.priority !== undefined || format.items.nonPriority !== null && format.items.nonPriority !== undefined);
        hasWeight = false; // Never export weight for express
      } else if (key === 'mbags') {
        product = 'M-Bags';
        // M-Bags is weight-only - always check weight structure, ignore items
        hasWeight = format && format.weight && (format.weight.priority !== null && format.weight.priority !== undefined || format.weight.nonPriority !== null && format.weight.nonPriority !== undefined);
        hasItems = false; // Never export items for mbags
      }
      
      // Generate CSV rows for available data - separate priority and non-priority
      if (['registered', 'insured', 'express', 'mbags'].includes(key)) {
        // Special case for registered, insured, express, mbags
        // These items need TWO rows each (Priority and Non-Priority)
        if (key === 'mbags') {
          // M-Bags has weight column filled, items column empty
          const weightPriority = format.weight?.priority ?? 0;
          const weightNonPriority = format.weight?.nonPriority ?? 0;
          const weightPriorityValue = formatCsvValueForExport(weightPriority);
          const weightNonPriorityValue = formatCsvValueForExport(weightNonPriority);
          csvRows.push(`${userPpo},${filterPpo},${year},Priority,${product},,${weightPriorityValue},0.000`);
          csvRows.push(`${userPpo},${filterPpo},${year},Non-Priority,${product},,${weightNonPriorityValue},0.000`);
        } else {
          // Registered, Insured, Express have items column filled, weight column empty
          // For outbound: do_from = userPpo, do_to = filterPpo
          const itemPriority = format.items?.priority ?? 0;
          const itemNonPriority = format.items?.nonPriority ?? 0;
          const itemPriorityValue = formatCsvValueForExport(itemPriority);
          const itemNonPriorityValue = formatCsvValueForExport(itemNonPriority);
          csvRows.push(`${userPpo},${filterPpo},${year},Priority,${product},,0.000,${itemPriorityValue}`);
          csvRows.push(`${userPpo},${filterPpo},${year},Non-Priority,${product},,0.000,${itemNonPriorityValue}`);
        }
      } else if (hasWeight && hasItems) {
        // Both weight and items available - generate separate rows for priority and non-priority
        const weightPriority = format.weight?.priority || 0;
        const weightNonPriority = format.weight?.nonPriority || 0;
        const itemPriority = format.items?.priority || 0;
        const itemNonPriority = format.items?.nonPriority || 0;
        
        // Priority row
        const weightValue = formatCsvValueForExport(weightPriority);
        const itemValue = formatCsvValueForExport(itemPriority);
        csvRows.push(`${userPpo},${filterPpo},${year},Priority,${product},,${weightValue},${itemValue}`);
        
        // Non-Priority row
        const weightValueNP = formatCsvValueForExport(weightNonPriority);
        const itemValueNP = formatCsvValueForExport(itemNonPriority);
        csvRows.push(`${userPpo},${filterPpo},${year},Non-Priority,${product},,${weightValueNP},${itemValueNP}`);
      } else if (hasWeight) {
        // Only weight available - generate separate rows for priority and non-priority
        const weightPriority = format.weight?.priority || 0;
        const weightNonPriority = format.weight?.nonPriority || 0;
        
        // Priority row
        csvRows.push(`${userPpo},${filterPpo},${year},Priority,${product},,${formatCsvValueForExport(weightPriority)},0.000`);
        // Non-Priority row
        csvRows.push(`${userPpo},${filterPpo},${year},Non-Priority,${product},,${formatCsvValueForExport(weightNonPriority)},0.000`);
      } else if (hasItems) {
        // Only items available - generate separate rows for priority and non-priority
        const itemPriority = format.items?.priority || 0;
        const itemNonPriority = format.items?.nonPriority || 0;
        
        // Priority row
        csvRows.push(`${userPpo},${filterPpo},${year},Priority,${product},,0.000,${formatCsvValueForExport(itemPriority)}`);
        // Non-Priority row
        csvRows.push(`${userPpo},${filterPpo},${year},Non-Priority,${product},,0.000,${formatCsvValueForExport(itemNonPriority)}`);
      }
    });
  }

  return csvRows.join('\n');
}

/**
 * Generate sample CSV for IPC CN61
 * @param currentUserPpo - Current user's designated operator
 * @param filterPpo - Filter PPO
 * @param year - Year
 * @returns Sample CSV string
 */
export function generateIpcCn61SampleCsv(currentUserPpo: string = 'CHA', filterPpo: string = 'USA', year: number = 2021): string {
  const sampleData = [
    // Inbound data - Priority and Non-Priority rows
    `${filterPpo},${currentUserPpo},${year},Priority,Format P,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Format P,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Format G,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Format G,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Format E,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Format E,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Postcards,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Postcards,,2.000,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Registered,,,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Registered,,,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Insured,,,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Insured,,,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,Express,,,2.000`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,Express,,,2.000`,
    `${filterPpo},${currentUserPpo},${year},Priority,M-Bags,,2.000,`,
    `${filterPpo},${currentUserPpo},${year},Non-Priority,M-Bags,,2.000,`,
    // Outbound data - Priority and Non-Priority rows
    `${currentUserPpo},${filterPpo},${year},Priority,Format P,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Format P,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Format G,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Format G,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Format E,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Format E,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Postcards,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Postcards,,2.000,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Registered,,,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Registered,,,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Insured,,,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Insured,,,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,Express,,,2.000`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,Express,,,2.000`,
    `${currentUserPpo},${filterPpo},${year},Priority,M-Bags,,2.000,`,
    `${currentUserPpo},${filterPpo},${year},Non-Priority,M-Bags,,2.000,`
  ];

  return `do_from,do_to,year,service,product,rg,td_rate_kg,td_rate_item\n${sampleData.join('\n')}`;
}

/**
 * Apply CSV data to IPC CN61 data structure
 * @param csvData - Parsed CSV data
 * @param baseData - Base CN61 data structure
 * @returns Updated CN61 data
 */
export function applyCsvDataToIpcCn61(csvData: IpcCn61RateData[], baseData: any): any {
  const updatedData = { ...baseData };
  
  // Only initialize structures for directions that have data
  const hasInboundData = csvData.some(row => row.direction === 'inbound');
  const hasOutboundData = csvData.some(row => row.direction === 'outbound');
  
  if (hasInboundData && !updatedData.request.inboundCn61Input) {
    updatedData.request.inboundCn61Input = {};
  }
  if (hasOutboundData && !updatedData.request.outboundCn61Input) {
    updatedData.request.outboundCn61Input = {};
  }
  
  csvData.forEach((row, index) => {
    const inputKey = row.direction === 'inbound' ? 'inboundCn61Input' : 'outboundCn61Input';
    const input = updatedData.request[inputKey];
    
    // Extract format from product name
    let formatKey = '';
    if (row.format.startsWith('Format ')) {
      const formatType = row.format.replace('Format ', '');
      formatKey = `format${formatType}`;
    } else if (row.format === 'Postcards') {
      formatKey = 'postcards';
    } else if (row.format === 'Registered') {
      formatKey = 'registered';
    } else if (row.format === 'Insured') {
      formatKey = 'insured';
    } else if (row.format === 'Express') {
      formatKey = 'express';
    } else if (row.format === 'M-Bags') {
      formatKey = 'mbags';
    }
    
    if (['formatP', 'formatG', 'formatE', 'formatX', 'postcards'].includes(formatKey)) {
      // Handle format fields with priority/non-priority
      if (!input[formatKey]) {
        input[formatKey] = {};
      }
      if (!input[formatKey][row.type]) {
        input[formatKey][row.type] = {};
      }
      const formatted = Number(row.rate).toFixed(3);
      if (row.service === 'Priority') {
        input[formatKey][row.type].priority = formatted;
      } else {
        input[formatKey][row.type].nonPriority = formatted;
      }
    } else if (['registered', 'insured', 'express'].includes(formatKey)) {
      // Handle items-only fields (Registered, Insured, Express) - only items structure
      if (!input[formatKey]) {
        input[formatKey] = {};
      }
      // Only allow items type for these fields
      if (row.type === 'items') {
        if (!input[formatKey].items) {
          input[formatKey].items = {};
        }
        const formatted = Number(row.rate).toFixed(3);
        if (row.service === 'Priority') {
          input[formatKey].items.priority = formatted;
        } else {
          input[formatKey].items.nonPriority = formatted;
        }
        // Ensure weight structure doesn't exist for these fields
        if (input[formatKey].weight) {
          delete input[formatKey].weight;
        }
      }
    } else if (formatKey === 'mbags') {
      // Handle weight-only field (M-Bags) - only weight structure
      if (!input[formatKey]) {
        input[formatKey] = {};
      }
      // Only allow weight type for this field
      if (row.type === 'weight') {
        if (!input[formatKey].weight) {
          input[formatKey].weight = {};
        }
        const formatted = Number(row.rate).toFixed(3);
        if (row.service === 'Priority') {
          input[formatKey].weight.priority = formatted;
        } else {
          input[formatKey].weight.nonPriority = formatted;
        }
        // Ensure items structure doesn't exist for this field
        if (input[formatKey].items) {
          delete input[formatKey].items;
        }
      }
    }
  });
  
  return updatedData;
}

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name of the file to download
 */
export function downloadIpcCn61Csv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}